import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as urlTemplate from 'url-template';

import {
  UserHistoryItem, BookingPoliciesPayload, BookingPoliciesResponse
} from '@user-history/entities/user-history-item';
import { userHistoryApi } from '@user-history/user-history.api';
import { 
  HistoryBookingDetail, 
  FullHistoryBookingDetail, 
  TravelOrderValidate 
} from '@user-history/entities/history-booking-detail';
import { CancellationSuccessType } from '@user-history/entities/cancellation.response';
import { CancellationReasonSumbitVM } from '@user-history/entities/cancellation-reasons';

@Injectable()
export class UserHistoryService {

  constructor(
    private http: HttpClient
  ) { }

  public fetchUserHistoryList(isDetailFetch: boolean = false): Observable<UserHistoryItem[]> {
    const headers = this.getHeaders(!isDetailFetch);
    return this.http.get<UserHistoryItem[]>(userHistoryApi.userhistory, { headers });
  }

  public fetchHistoryBookingDetail(detailId: string): Observable<HistoryBookingDetail> {
    const headers = this.getHeaders();
    const url = urlTemplate.parse(userHistoryApi.userDetailBooking).expand({
      detailId: detailId
    });
    return this.http.get<HistoryBookingDetail>(url, { headers });
  }

  public fetchLastUserReservationDetail(): Observable<HistoryBookingDetail> {
    const headers = this.getHeaders();
    return this.http.get<HistoryBookingDetail>(userHistoryApi.history_detail.last, { headers });
  }

  public fetchRappiItineraryByOrderDetail(
    orderId: string, session: string, userId: number
  ): Observable<FullHistoryBookingDetail> {
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${session}`, 'auth_user': `${userId}`, });
    const url = urlTemplate.parse(userHistoryApi.rappi_itinerary.by_order_id).expand({ orderId });
    return this.http.get<FullHistoryBookingDetail>(url, { headers });
  }

  public fetchBookingPolicies(payload: BookingPoliciesPayload): Observable<BookingPoliciesResponse> {
    const headers = this.getHeaders(false);
    return this.http.post<BookingPoliciesResponse>(userHistoryApi.air_policies, payload, { headers });
  }

  public doCancellation(
    itineraryId: string, reason: CancellationReasonSumbitVM, userToken: string
  ): Observable<CancellationSuccessType> {
    const payload = {
      itineraryId: itineraryId,
      nssUserSessionToken: userToken,
      cancellations_reasons :{
        key: reason?.reason?.key ,
        value: reason?.reason?.value ,
        comments: reason?.comment
      }
    };
    const headers = this.getHeaders();
    return this.http.post<(CancellationSuccessType)>(userHistoryApi.cancellation, payload, { headers });
  }

  public resendVoucherByItinerary(detailId: number, email: string, session: string): Observable<void> {
    const headers = this.getHeaders();
    return this.http.post<void>(userHistoryApi.resend_voucher, {
      TravelItineraryId: detailId,
      Email: email,
      SessionToken: session
    }, { headers });
  }

  public getDetailByValidate(detailId: string): Observable<FullHistoryBookingDetail> {
    const headers = this.getHeaders();
    const url = urlTemplate.parse(userHistoryApi.history_detail.get_detail_by_validate).expand({
      detailId
    });
    return this.http.get<FullHistoryBookingDetail>(url, { headers });
  }

  public getDetailById(detailId: string): Observable<FullHistoryBookingDetail> {
    const headers = this.getHeaders();
    const url = urlTemplate.parse(userHistoryApi.history_detail.get_detail_by_id).expand({
      detailId
    });
    return this.http.get<FullHistoryBookingDetail>(url, { headers });
  }

  public getValidatePaymentItinerary(detailId: number): Observable<TravelOrderValidate> {
    const headers = this.getHeaders();
    const url = urlTemplate.parse(userHistoryApi.history_detail.validate_payment_itinerary).expand({
      detailId: detailId
    });
    return this.http.get<TravelOrderValidate>(url, { headers });
  }

  private getHeaders(ignoreLoadingBar: boolean = true, applicationID: string = null): HttpHeaders {
    const params = {};
    if (applicationID) {
      params['X_Application_ID'] = applicationID;
    }
    if (ignoreLoadingBar) {
      params['ignoreLoadingBar'] = '';
    }
    return new HttpHeaders(params);
  }
  
}
